import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import Reveal from "@/components/Reveal";
import { TICKETS_URL, VENUE_URL, VENUE_NAME } from "@/lib/tickets";

const featured = [
  { src: "/release/tribal.jpg", title: "Tribal Goddess", aspect: "portrait" },
  { src: "/artworks/light-language.jpg", title: "Light Language", aspect: "square" },
  { src: "/release/p101-iv.jpg", title: "Universal Soul of Stars", aspect: "portrait" },
];

export default function Home() {
  const [scrolled, setScrolled] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrolled(Math.min(1, window.scrollY / 800));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Layout>
      <main data-testid="home-page" className="bg-[color:var(--bone)]">
        {/* Threshold hero */}
        <section
          data-testid="home-hero"
          className="relative h-[100svh] w-full overflow-hidden bg-black text-[color:var(--bone)]"
        >
          <div
            className="absolute inset-0"
            style={{ transform: `translate3d(0, ${scrolled * 50}px, 0)` }}
          >
            <img
              src="/hero/projection.jpg"
              alt="Nick Fraser — Chapter 2 projection, Shoreditch"
              className="h-full w-full object-cover slow-zoom"
              style={{ opacity: 0.95 }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.75) 100%)",
              }}
            />
          </div>

          <div className="absolute bottom-0 left-0 right-0 z-10 px-6 md:px-12 pb-16 md:pb-20">
            <div className="max-w-[1500px] mx-auto">
              <p className="hairline mb-6 md:mb-8 opacity-80" data-testid="home-eyebrow">
                The East End Van Gogh
              </p>
              <h1
                className="font-wordmark leading-[0.95] tracking-wide text-[18vw] md:text-[12vw] lg:text-[11rem]"
                data-testid="home-title"
              >
                Nick Fraser
              </h1>
              <p
                className="font-serif italic text-xl md:text-2xl mt-5 md:mt-7 opacity-85 max-w-2xl"
                data-testid="home-subtitle"
              >
                1946 — 2023.&nbsp; Painted in private. Released in chapters.
              </p>
              <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
                <a
                  href={TICKETS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-hero-tickets"
                  className="hairline border border-[color:var(--bone)] px-6 py-3 hover:bg-[color:var(--bone)] hover:text-[color:var(--ink)] transition-colors duration-500"
                >
                  31 July · Book Tickets →
                </a>
                <Link
                  to="/chapter-ii"
                  className="hairline opacity-80 hover:opacity-100 transition-opacity"
                  data-testid="home-hero-chapter-link"
                >
                  Enter Chapter 2
                </Link>
              </div>
            </div>
          </div>

          <div className="hidden md:flex absolute bottom-8 right-6 md:right-12 z-10 flex-col items-center gap-3">
            <span className="hairline opacity-60">Scroll</span>
            <span className="enter-line" />
          </div>
        </section>

        {/* The Discovery */}
        <section className="bone-section py-32 md:py-48" data-testid="home-discovery">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
            <Reveal className="md:col-span-7" slow>
              <p className="hairline mb-10 opacity-60">I. About Nick</p>
              <p className="font-serif italic text-3xl md:text-5xl leading-[1.18]">
                Behind the working life of Nick Fraser was a private artistic
                universe of remarkable intensity.
              </p>
              <p className="font-serif italic text-xl md:text-2xl leading-[1.4] mt-10 opacity-80 max-w-3xl">
                Born in East London in 1946, Fraser spent more than three
                decades working at the Tate &amp; Lyle factory while quietly
                developing a visual language of his own. His paintings move
                between the cosmic and the bodily, the sacred and the
                humorous, the tender and the confrontational.
              </p>
              <p className="font-serif italic text-xl md:text-2xl leading-[1.4] mt-8 opacity-80 max-w-3xl">
                A romantic recluse with deep East End roots, Fraser created
                outside the art world, guided by instinct, faith and an
                urgent need to make sense of existence.
              </p>
              <p className="font-serif italic text-xl md:text-2xl leading-[1.4] mt-8 opacity-90 max-w-3xl">
                Today, his archive is being revealed in chapters.
              </p>
              <Link
                to="/the-artist"
                className="link-quiet font-serif italic text-lg md:text-xl mt-12 inline-block"
                data-testid="home-discovery-link"
              >
                Read his story
              </Link>
            </Reveal>

            <Reveal className="md:col-span-5 md:pt-24" slow delay={140}>
              <figure data-testid="home-nick-portrait">
                <img
                  src="/hero/nick-portrait.jpg"
                  alt="Nick Fraser"
                  loading="lazy"
                  className="w-full h-auto block"
                />
                <figcaption className="hairline opacity-55 mt-4">
                  Nick Fraser · 1946 — 2023
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </section>

        {/* Featured works */}
        <section className="bone-section pt-0 pb-32 md:pb-48" data-testid="home-featured">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12">
            <Reveal className="mb-16 md:mb-24">
              <p className="hairline opacity-60 mb-6">II. The Work</p>
              <h2 className="font-serif italic text-4xl md:text-5xl leading-[1.05] max-w-2xl">
                A body of work — coming, slowly, into the light.
              </h2>
            </Reveal>

            <div className="grid grid-cols-12 gap-6 md:gap-12">
              {featured.map((art, i) => (
                <Reveal
                  key={art.title}
                  delay={i * 120}
                  className={
                    i === 0
                      ? "col-span-12 md:col-span-6 md:col-start-1"
                      : i === 1
                      ? "col-span-12 md:col-span-5 md:col-start-8 md:mt-32"
                      : "col-span-12 md:col-span-6 md:col-start-2 md:mt-16"
                  }
                  slow
                >
                  <figure>
                    <img
                      src={art.src}
                      alt={art.title}
                      loading="lazy"
                      className="w-full h-auto block"
                      data-testid={`home-featured-${i}`}
                    />
                    <figcaption className="hairline opacity-60 mt-4">
                      {art.title}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-24 md:mt-32 text-right">
              <Link
                to="/work"
                className="link-quiet font-serif italic text-xl md:text-2xl"
                data-testid="home-work-link"
              >
                See the catalogue →
              </Link>
            </Reveal>
          </div>
        </section>

        {/* Chapter II teaser */}
        <section
          data-testid="home-chapter-ii"
          className="dark-section relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-30">
            <img
              src="/release/tribal.jpg"
              alt=""
              className="w-full h-full object-cover"
              aria-hidden
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(18,18,18,0.65) 0%, rgba(18,18,18,0.95) 100%)",
              }}
            />
          </div>
          <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 py-32 md:py-48">
            <Reveal slow>
              <p className="hairline opacity-60 mb-8">III. Chapter 2</p>
              <h2 className="font-serif italic text-4xl md:text-6xl leading-[1.05] max-w-3xl">
                31 July 2026 —{" "}
                <a
                  href={VENUE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="link-quiet"
                  data-testid="home-venue-link"
                >
                  {VENUE_NAME}
                </a>
                .
              </h2>
              <p className="font-serif italic text-xl md:text-2xl opacity-80 mt-8 max-w-2xl">
                A one-day exhibition exploring identity, masculinity, memory
                and the unspoken life within the work. Selected paintings,
                archive material, and a limited release of prints.
              </p>
              <Link
                to="/chapter-ii"
                className="link-quiet font-serif italic text-xl md:text-2xl mt-14 inline-block"
                data-testid="home-chapter-ii-link"
              >
                Enter the Release
              </Link>
              <a
                href={TICKETS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-chapter-ii-tickets"
                className="link-quiet font-serif italic text-xl md:text-2xl mt-14 md:mt-14 mt-6 inline-block md:ml-12"
              >
                Book Tickets →
              </a>
            </Reveal>
          </div>
        </section>

        {/* Curators */}
        <section className="bone-section py-32 md:py-48" data-testid="home-curators">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12">
            <Reveal className="max-w-3xl mb-20">
              <p className="hairline opacity-60 mb-6">IV. The Estate</p>
              <h2 className="font-serif italic text-4xl md:text-5xl leading-[1.05]">
                A friendship, a family, an archive.
              </h2>
              <p className="font-serif italic text-xl md:text-2xl opacity-80 mt-8 leading-[1.4]">
                The Nick Fraser estate is held in trust by Mark O&apos;Shaughnessy,
                Nick&apos;s lifelong friend, muse and advocate, and curated by
                Marcus O&apos;Shaughnessy. Together, their work is rooted in
                friendship, memory and responsibility: to protect Nick&apos;s
                archive, share his story with care, and bring his unseen work
                into the light.
              </p>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
              {[
                {
                  name: "Mark O'Shaughnessy",
                  role: "Lifelong friend, muse & custodian",
                  image: "/curators/mark.jpg",
                  paras: [
                    "Nick's lifelong friend, muse and custodian, Mark was one of the few people Nick trusted enough to speak openly with about his life, beliefs and work. Their friendship became central to the preservation of the archive.",
                    "As custodian of Nick's estate, Mark carries the memory of the man behind the paintings, protecting the work as someone who knew Nick intimately and stood beside him throughout his life.",
                  ],
                },
                {
                  name: "Marcus O'Shaughnessy",
                  role: "Curator · Founder, Imaginative Reaction CIC",
                  image: "/curators/marcus.jpg",
                  paras: [
                    "Marcus curated Nick Fraser's first public exhibition, Chapter 1, at The Crypt Gallery, London, and continues to shape each stage of the estate's release.",
                    "From this journey, Marcus founded Imaginative Reaction CIC — an arts organisation created to support unseen and unheard artists, giving others the platform Nick was never afforded in his lifetime. Chapter 2 continues that purpose, bringing Nick's work back into the world while widening the path for others.",
                  ],
                },
              ].map((c, i) => (
                <Reveal key={c.name} delay={i * 140} slow>
                  <div data-testid={`home-curator-${i}`}>
                    <figure className="overflow-hidden bg-[color:var(--bone-deep)]">
                      <img
                        src={c.image}
                        alt={c.name}
                        loading="lazy"
                        className="w-full h-auto block aspect-[4/5] object-cover"
                      />
                    </figure>
                    <div className="mt-6">
                      <p className="hairline opacity-55">{c.role}</p>
                      <h3 className="font-serif italic text-3xl md:text-4xl mt-2">
                        {c.name}
                      </h3>
                      <div className="mt-5 space-y-4">
                        {c.paras.map((p, j) => (
                          <p
                            key={j}
                            className="font-serif italic text-base md:text-lg opacity-80 leading-relaxed max-w-md"
                          >
                            {p}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Press whisper */}
        <section className="bone-section pb-32 md:pb-48" data-testid="home-press">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <div className="rule opacity-30 mb-20" />
            <Reveal slow>
              <p className="hairline opacity-60 mb-6">V. Press</p>
              <p className="font-serif italic text-3xl md:text-5xl leading-[1.15]">
                Featured in Raw Vision — Issue 121.
              </p>
              <p className="font-serif italic text-xl md:text-2xl opacity-70 mt-6 max-w-2xl">
                The international journal of intuitive, outsider and visionary art.
              </p>
              <Link
                to="/press"
                className="link-quiet font-serif italic text-lg md:text-xl mt-10 inline-block"
                data-testid="home-press-link"
              >
                Read the feature
              </Link>
            </Reveal>
          </div>
        </section>

        {/* Visitor impression */}
        <section className="dark-section py-32 md:py-48" data-testid="home-impression">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal slow>
              <p className="hairline opacity-60 mb-8">VI. An Impression</p>
              <p className="font-serif italic text-2xl md:text-4xl leading-[1.25]">
                &ldquo;Spiritual, tribal, ethereal — a faintly ominous other-worldly
                experience. The dramatic legacy of an unrecognised yet
                extraordinary talent who died in 2023, unknown.&rdquo;
              </p>
              <p className="hairline opacity-60 mt-10" data-testid="home-impression-attr">
                Andrianne · Chapter 1, The Crypt Gallery, London
              </p>
            </Reveal>
          </div>
        </section>

        {/* Chapter 1 short film */}
        <section
          className="bone-section py-32 md:py-48"
          data-testid="home-chapter-1-film"
        >
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
            <Reveal className="md:col-span-5">
              <p className="hairline opacity-60 mb-6">VII. The Short Film</p>
              <h2 className="font-serif italic text-4xl md:text-5xl leading-[1.05]">
                The Legacy of Nick Fraser.
              </h2>
              <p className="font-serif italic text-lg md:text-xl opacity-80 mt-8 max-w-md leading-relaxed">
                An eleven-minute introduction to Nick Fraser&apos;s life, work and
                artistic journey — featuring archive material and insight into
                the East End artist whose private world is now being released
                in chapters.
              </p>
              <a
                href="https://www.youtube.com/watch?v=bjPlwtp4zzg"
                target="_blank"
                rel="noreferrer"
                className="link-quiet font-serif italic text-lg md:text-xl mt-10 inline-block"
                data-testid="home-film-link"
              >
                Watch on YouTube →
              </a>
            </Reveal>
            <Reveal className="md:col-span-7" slow>
              <div
                className="relative w-full overflow-hidden bg-black"
                style={{ aspectRatio: "16 / 9" }}
              >
                <iframe
                  src="https://www.youtube-nocookie.com/embed/bjPlwtp4zzg?rel=0&modestbranding=1"
                  title="The Legacy of Nick Fraser"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  data-testid="home-film-iframe"
                />
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </Layout>
  );
}
